import { Sparkles, Star } from "lucide-react";
import { useState } from "react";
import FireworkAnimation from "../../../shared/animations/firework/fireworkAnimation";

export default function Roulette({
    prizes,
    showList = true,
    subtitle,
    footer,
    allowSpin = true,
    onSelectPrize = selectedPrize => { },
    onFinish = () => { },
    spinAmount = 1,
}) {

    const spinDuration = 16;

    const [isSpinning, setIsSpinning] = useState(false)
    const [rotation, setRotation] = useState(0)
    const [currentPrize, setCurrentPrize] = useState(null);
    const [prizesWon, setPrizesWon] = useState([]);

    const [spinsLeft, setSpinsLeft] = useState(spinAmount);

    const hasWonPrize = (prizeKey) => prizesWon.some(p => p.key === prizeKey);
    const isLastPrizeWon = (prizeKey) => prizesWon[prizesWon.length - 1].key === prizeKey;

    const getMultiplePrizesText = () => {

        const automaticPrizes = prizesWon.filter(p => p.deliveryType === 'automatic');
        const manualPrizes = prizesWon.filter(p => p.deliveryType === 'manual');

        const allAutomatic = automaticPrizes.length === prizesWon.length;

        if (allAutomatic)
            return 'Os seus prêmios já estão disponíveis na plataforma e pronto para uso!';

        const allManual = manualPrizes.length === prizesWon.length;

        if (allManual)
            return 'Estamos organizando a entrega com todo carinho e entraremos em contato em breve para alinharmos os detalhes';

        return `Alguns dos seus prêmios já estão disponíveis na plataforma e pronto para uso! Consulte o email enviado para mais informações sobre os prêmios adicionais!`;
    }

    const getPrizeText = () => {
        if (currentPrize.deliveryType === 'manual')
            return 'Estamos organizando a entrega com todo carinho e entraremos em contato em breve para alinharmos os detalhes';

        return 'O seu prêmio já está disponível na plataforma e pronto para uso!'
    };

    const calculateWeightedRotation = () => {
        const totalWeight = prizes.reduce((sum, prize) => sum + prize.percentChance, 0)

        const random = Math.random() * totalWeight

        let accumulatedWeight = 0
        let selectedIndex = 0

        for (let i = 0; i < prizes.length; i++) {
            accumulatedWeight += prizes[i].percentChance
            if (random <= accumulatedWeight) {
                selectedIndex = i
                break
            }
        }

        setCurrentPrize(prizes[selectedIndex]);
        setPrizesWon(prev => [...prev, prizes[selectedIndex]]);

        const segmentSize = 360 / prizes.length

        const segmentRandomness = (Math.random() * 0.8 + 0.1) * segmentSize;

        const additionalRotations = 10;

        const currentSingleRotation = rotation % 360;

        const baseRotation = (segmentSize * selectedIndex) + segmentRandomness + currentSingleRotation;

        const newRotation = -(baseRotation + (360 * additionalRotations)) + rotation;

        return {
            newRotation: newRotation,
            prize: prizes[selectedIndex]
        }
    }

    const spin = () => {
        if (isSpinning || prizeIsSelected || !allowSpin) return;

        setIsSpinning(true)
        const { newRotation, prize } = calculateWeightedRotation()
        setRotation(newRotation)

        setTimeout(() => {
            setIsSpinning(false);
            setSpinsLeft(prev => prev - 1);
            onSelectPrize(prize);
        }, spinDuration * 1000)
    }

    const renderPrizeSlice = (prize, index) => {
        const angle = (360 / prizes.length) * index
        const nextAngle = (360 / prizes.length) * (index + 1)

        const centerX = 180
        const centerY = 180
        const radius = 170

        const startAngleRad = ((angle - 90) * Math.PI) / 180
        const endAngleRad = ((nextAngle - 90) * Math.PI) / 180

        const x1 = centerX + radius * Math.cos(startAngleRad)
        const y1 = centerY + radius * Math.sin(startAngleRad)
        const x2 = centerX + radius * Math.cos(endAngleRad)
        const y2 = centerY + radius * Math.sin(endAngleRad)

        const largeArcFlag = nextAngle - angle > 180 ? 1 : 0

        const pathData = [
            `M ${centerX} ${centerY}`,
            `L ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
            "Z",
        ].join(" ")

        const textAngle = angle + 360 / prizes.length / 2 - 90
        const textAngleRad = (textAngle * Math.PI) / 180
        const textRadius = radius * 0.65
        const textX = centerX + textRadius * Math.cos(textAngleRad)
        const textY = centerY + textRadius * Math.sin(textAngleRad)

        return (
            <g key={index}>
                <path
                    d={pathData}
                    fill={prize.color}
                    stroke="#ffffff"
                    strokeWidth="2"
                    style={{
                        filter: `drop-shadow(0 2px 4px rgba(0,0,0,0.2)) ${!isSpinning && currentPrize && currentPrize?.label !== prize.label ? "brightness(0.6)" : ""}`,
                        transition: "filter 0.3s ease-in-out"
                    }}
                />
                <text
                    x={textX}
                    y={textY}
                    fill="white"
                    fontSize="13"
                    fontWeight="bold"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{
                        textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
                        fontFamily: "system-ui, sans-serif",
                    }}
                >
                    {prize.label.split(" ").map((word, i) => (
                        <tspan key={i} x={textX} dy={i === 0 ? 0 : 14}>
                            {word}
                        </tspan>
                    ))}
                </text>
            </g>
        )
    };

    const prizeIsSelected = spinsLeft <= 0 && !isSpinning;

    return (
        <div className={`text-center bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 ${prizeIsSelected ? "from-amber-800 via-orange-800 to-red-800" : ""} transition-[background] duration-1000 text-white p-8 rounded-3xl shadow-2xl max-w-6xl mx-auto relative overflow-hidden`}>
            <div className="flex items-center justify-center gap-4 mb-6">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Sparkles className="w-8 h-8" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">Roleta da Sorte</h2>
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Sparkles className="w-8 h-8" />
                </div>
            </div>
            <p className="text-lg md:text-xl mb-8">
                {subtitle}
            </p>

            <div className="flex flex-col lg:flex-row items-center justify-center gap-12 mb-8">
                <div className="flex flex-col items-center justify-center gap-4">

                    {spinAmount > 1 && (
                        <div className="text-xl font-medium mb-4">
                            <span className="font-bold bg-white/20 px-2 py-1 rounded">{spinsLeft}</span>
                            {" "}Giros Restantes
                        </div>
                    )}

                    <div className="relative w-80 h-80 md:w-96 md:h-96">
                        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-30">
                            <div className="w-6 h-8 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-b-full shadow-lg border-2 border-yellow-300 flex items-end justify-center">
                                <div className="w-0 h-0 border-l-3 border-r-3 border-t-4 border-l-transparent border-r-transparent border-t-yellow-600 mb-1"></div>
                            </div>
                        </div>

                        <div className="absolute inset-0 w-80 h-80 md:w-96 md:h-96 rounded-full bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 p-2 shadow-2xl">
                            <div className="w-full h-full rounded-full bg-gradient-to-r from-gray-800 to-gray-900 p-3 shadow-inner">
                                <div className="relative w-full h-full rounded-full overflow-hidden shadow-2xl">
                                    <svg
                                        className={`w-full h-full ${isSpinning ? "" : ""}`}
                                        style={{
                                            transform: `rotate(${rotation}deg)`,
                                            transition: `transform ${spinDuration}s cubic-bezier(0.17, 0.84, 0.44, 1)`,
                                            filter: "drop-shadow(0 0 10px rgba(0,0,0,0.3))",
                                        }}
                                        viewBox="0 0 360 360"
                                    >
                                        {prizes.map(renderPrizeSlice)}
                                    </svg>


                                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                        <div className="relative w-16 h-16 md:w-20 md:h-20">
                                            {!currentPrize && !isSpinning && allowSpin && <div className="absolute w-full h-full bg-yellow-500 rounded-full -z-10 animate-[ping_2s_ease-in-out_infinite]" />}

                                            <div
                                                className={`
                                            w-full h-full shadow-[0_0_8px_8px_#0003 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center border-4 border-white
                                            cursor-pointer hover:scale-110 hover:shadow-[0_0_8px_6px_#0006]
                                            active:scale-95
                                            z-10
                                            ${isSpinning || prizeIsSelected ? "opacity-0 pointer-events-none" : "opacity-100"}
                                            ${!allowSpin || isSpinning ? "pointer-events-none" : ""}
                                            `}
                                                style={{
                                                    transition: "transform 0.1s ease-in-out, box-shadow 0.1s ease-in-out, opacity 0.1s ease-in-out"
                                                }}
                                                onClick={spin}
                                            >
                                                <Star className="w-8 h-8 md:w-10 md:h-10 text-white fill-white drop-shadow-lg" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {allowSpin && (
                        <button
                            className={`
                            text-white text-xl font-semibold bg-gradient-to-r shadow-lg from-yellow-400 to-yellow-600 px-8 py-3 rounded-full hover:scale-105 cursor-pointer border-4 border-white
                            ${isSpinning || prizeIsSelected ? "pointer-events-none opacity-0" : "opacity-100"}
                            `}
                            style={{ transition: 'transform .1s ease-in-out, opacity .1s ease-in-out' }}
                            onClick={spin}
                        >
                            Girar Roleta
                        </button>
                    )}
                </div>

                {prizeIsSelected && (
                    <div className="fixed top-0 left-0 w-screen h-screen pointer-events-none">
                        <FireworkAnimation />
                    </div>
                )}

                {showList && (
                    <div className="grid grid-cols-1 gap-3 max-w-xs">
                        <h3 className="text-xl font-bold mb-4 text-center">Prêmios Disponíveis</h3>
                        {prizes.map((prize, index) => (
                            <div
                                key={index}
                                className={`${!isSpinning && hasWonPrize(prize.key) || hasWonPrize(prize.key) && !isLastPrizeWon(prize.key) ? 'bg-white/90 text-gray-800' : 'bg-white/15'} rounded-lg p-3 text-center border border-white/20 flex items-center gap-3  transition-all`}
                            >
                                <div
                                    className="w-5 h-5 rounded-full shadow-lg border-2 border-white/30"
                                    style={{
                                        backgroundColor: prize.color,
                                    }}
                                />
                                <p className="text-sm font-medium flex-1 text-left">
                                    {prize.label}
                                    {prize.subLabel && <span className="opacity-75 font-normal"> {prize.subLabel}</span>}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {footer && <p className="text-lg font-semibold">{footer}</p>}

            {prizeIsSelected && (
                <div className="flex flex-col items-center justify-center">
                    <div className="max-w-2xl text-xl leading-10">
                        Você ganhou <span>
                            {prizesWon.map((prize, index) => (
                                <span key={prize.key}>
                                    <span className="font-bold bg-white/20 px-2 py-1 rounded">{prize.label}{prize.subLabel && <span className="opacity-75 font-light"> {prize.subLabel}</span>}</span>
                                    {index < prizesWon.length - 1 && <span className="opacity-75 font-light">, </span>}
                                </span>
                            ))}
                        </span>

                        <br />
                        <br />
                        <span className="text-lg">
                            {prizesWon.length > 1 ? getMultiplePrizesText() : getPrizeText()}
                        </span>
                    </div>
                    <div className="mt-6">
                        <button className="bg-white rounded-lg text-lg text-orange-700 px-4 py-2 font-medium hover:scale-105 transition-transform duration-300" onClick={onFinish}>
                            Continuar
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}